import React, {useEffect, useState} from 'react';
import {Modal, Spin, message, Divider, Typography} from 'antd';
import roleRestService from "../../services/role-rest.service";
import UserRoles from "./UserRoles";
import AssignRole from "./AssignRole";

const {Title} = Typography;

const ManageRolesModal = ({open, user, onClose}) => {
    const [allRoles, setAllRoles] = useState([]);
    const [userRoles, setUserRoles] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [isUpdating, setUpdating] = useState(false);

    const loadRoles = async () => {
        if (!user?.id) return;
        setLoading(true);
        try {
            const [all, assigned] = await Promise.all([
                roleRestService.getAllRoles(),
                roleRestService.getUserRoles(user.id)
            ]);
            setAllRoles(all);
            setUserRoles(assigned);
        } catch (error) {
            message.error({content: 'Failed to load roles: ' + error, duration: 5});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && user?.id) {
            loadRoles();
        }
        if (!open) {
            setAllRoles([]);
            setUserRoles([]);
        }
    }, [open, user?.id]);

    const handleAssign = async (roleName) => {
        setUpdating(true);
        message.loading({content: 'Assigning role...', key: 'role-update'});
        try {
            await roleRestService.assignRole(user.id, roleName);
            message.success({content: 'Role assigned successfully!', key: 'role-update', duration: 3});
            const updatedRoles = await roleRestService.getUserRoles(user.id);
            setUserRoles(updatedRoles);
        } catch (error) {
            message.error({content: 'Failed to assign role: ' + error, key: 'role-update', duration: 5});
        } finally {
            setUpdating(false);
        }
    };

    const handleRemove = async (roleName) => {
        setUpdating(true);
        message.loading({content: 'Removing role...', key: 'role-update'});
        try {
            await roleRestService.removeRole(user.id, roleName);
            message.success({content: 'Role removed successfully!', key: 'role-update', duration: 3});
            const updatedRoles = await roleRestService.getUserRoles(user.id);
            setUserRoles(updatedRoles);
        } catch (error) {
            message.error({content: 'Failed to remove role: ' + error, key: 'role-update', duration: 5});
        } finally {
            setUpdating(false);
        }
    };

    const userRoleNames = userRoles.map((r) => r.name);
    const availableRoles = allRoles.filter((r) => !userRoleNames.includes(r.name));

    return (
        <Modal
            open={open}
            title={`Manage Roles — ${user?.firstName || ''} ${user?.lastName || ''}`}
            onCancel={onClose}
            footer={null}
            width={600}
            destroyOnClose
        >
            <Spin spinning={isLoading}>
                <Title level={5}>Assign New Role</Title>
                <AssignRole
                    availableRoles={availableRoles}
                    onAssign={handleAssign}
                    isUpdating={isUpdating}
                />

                <Divider/>

                <Title level={5}>Assigned Roles</Title>
                <UserRoles
                    roles={userRoles}
                    onRemove={handleRemove}
                    isUpdating={isUpdating}
                />
            </Spin>
        </Modal>
    );
};

export default ManageRolesModal;
